import React, { useState, useEffect, useMemo } from 'react';
import FilterRow from './FilterRow';
import './MultiSelectFilterUI.css';
import axios from '../../../Axios';
import { Urls } from '../../../Urls';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import moment from 'moment';


const configCache = new Map(); // Keep as fallback or remove if completely replaced

const MultiSelectFilterUI = ({
    onFilterApply,
    onReset,
    onClose,
    isLoading = false,
    module = '',
    action = '',
    defaultFields = [],
    defaultSortOptions = [],
    initialPayload = null,
    showSearch = true,
    filterBaseUrl = null,           
    uniqueValuesMasterType = null,
    extraPayload = {},
    hiddenFields = [],
    showViewSelection = true,
    filterField = '',
    hideItemsPerPage = false,
    defaultLimit = null
}) => {
    const queryClient = useQueryClient();
    const filtersBaseUrl = filterBaseUrl || Urls.filters;
    // Current active filters
    const [filters, setFilters] = useState([{
        id: Date.now(),
        key: '',
        label: '',
        type: '',
        op: '',
        values: [],
        allowedOperators: []
    }]);

    // Internal state for extra controls
    const [filtersOperator, setFiltersOperator] = useState('and');
    const [searchText, setSearchText] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: (defaultSortOptions && defaultSortOptions.length > 0) ? defaultSortOptions[0].value : 'created_on',
        order: 'desc'
    });
    const isIndentModule = module && (module.includes('indent_tracking') || module.includes('indent_request') || module.includes('indent') || module === 'spot');
    const initialLimit = defaultLimit || (isIndentModule ? 10 : 50);
    const [pagination, setPagination] = useState({ skip: 0, limit: initialLimit });
    const [viewSelection, setViewSelection] = useState('');
    useEffect(() => {
        if (initialPayload?.view) {
            setViewSelection(initialPayload.view);
        } else if (extraPayload?.view) {
            setViewSelection(extraPayload.view);
        }
    }, [initialPayload?.view, extraPayload?.view]);

    const { 
        data: config, 
        isLoading: isConfigQueryLoading 
    } = useQuery({
        queryKey: ['filterConfig', module, action],
        queryFn: async () => {
            const endpoint = `${filtersBaseUrl}?${filterField ? `field=${filterField}&` : ''}module=${module}&action=${action}`;
            const response = await axios.get(endpoint);
            return response.data;
        },
        staleTime: 1000 * 60 * 10, // 10 minutes cache
        enabled: !!module && !!action
    });

    const { availableFields, availableSorts, viewFields } = useMemo(() => {
        if (!config) return { availableFields: defaultFields || [], availableSorts: defaultSortOptions || [], viewFields: [] };

        const rawData = config;
        let filtersArray = [];
        if (Array.isArray(rawData)) {
            filtersArray = rawData;
        } else if (rawData.filters && Array.isArray(rawData.filters)) {
            filtersArray = rawData.filters;
        } else if (rawData.data && Array.isArray(rawData.data.filters)) {
            filtersArray = rawData.data.filters;
        }

        const VIEW_KEYS = ['my_indent', 'company_indent', 'my_indents', 'company_indents', 'my_enquries', 'company_enquries', 'my_enquiries', 'company_enquiries', 'user_view', 'company_view'];

        const mappedFields = filtersArray
            .filter(config => {
                const key = config.field || config.key || config.db_field;
                if (hiddenFields.includes(key)) return false;
                if (config.ui && config.ui.widget === 'button') return false;
                if (VIEW_KEYS.includes(key?.toLowerCase())) return false;
                
                if ((key === 'users' || key === 'user' || key === 'user_id') && viewSelection === 'user_view') return false;
                
                return true;
            })
            .map(config => ({
                key: config.field || config.key || config.db_field,
                label: config.label || config.field || config.key,
                type: config.type === 'string' ? 'text' : (config.type || 'text'),
                operators: config.operators || [],
                values: (config.values || []).map(v => Array.isArray(v) && v.length > 0 ? v[0] : v)
            }));

        let sortsArray = [];
        if (rawData.sort_fields && Array.isArray(rawData.sort_fields)) {
            sortsArray = rawData.sort_fields;
        } else if (rawData.sorts && Array.isArray(rawData.sorts)) {
            sortsArray = rawData.sorts;
        }

        const isIndentRequest = module === 'indent_request';
        let finalSorts = isIndentRequest ? [{ value: 'created_date', label: 'Created Date' }] : defaultSortOptions;
        
        if (!isIndentRequest && sortsArray.length > 0) {
            finalSorts = sortsArray.map(s => ({
                value: s.value || s.key || s.field,
                label: s.label || s.key || s.field
            }));
        } else if (!isIndentRequest && finalSorts.length === 0 && mappedFields.length > 0) {
            finalSorts = mappedFields.map(f => ({
                value: f.key,
                label: f.label
            }));
        }

        const viewFields = filtersArray
            .filter(config => {
                const key = config.field || config.key || config.db_field;
                return (config.ui && config.ui.widget === 'button') || VIEW_KEYS.includes(key?.toLowerCase());
            })
            .map(config => {
                const key = config.field || config.key || config.db_field;
                const lowerKey = key?.toLowerCase();
                let val = 'user_view';
                if (lowerKey && (lowerKey.includes('company') || lowerKey.includes('all'))) {
                    val = 'company_view';
                }
                
                return {
                    key: key,
                    label: config.label || config.field || config.key,
                    value: val
                };
            });

        const booleanFieldsToEnsure = [
            { key: 'lr_uploaded', label: 'LR Uploaded', type: 'boolean', operators: ['exist', 'not exist'] },
            { key: 'pod_uploaded', label: 'POD Uploaded', type: 'boolean', operators: ['exist', 'not exist'] },
            { key: 'invoice_uploaded', label: 'Invoice Uploaded', type: 'boolean', operators: ['exist', 'not exist'] }
        ];

        let finalMappedFields = [...mappedFields];
        if (module && module.includes('indent_tracking')) {
            booleanFieldsToEnsure.forEach(bf => {
                if (!finalMappedFields.some(f => f.key === bf.key)) {
                    finalMappedFields.push(bf);
                }
            });
        }

        return { availableFields: finalMappedFields, availableSorts: finalSorts, viewFields };
    }, [config, defaultFields, defaultSortOptions, hiddenFields, viewSelection]);

    const hasInitializedRef = React.useRef(false);

    /**
     * Initialize filters from initialPayload when availableFields are loaded
     */
    useEffect(() => {
        if (initialPayload && availableFields.length > 0 && !hasInitializedRef.current) {
            hasInitializedRef.current = true;
                let restoredFilters = [];

                if (initialPayload.filters && initialPayload.filters.length > 0) {
                    restoredFilters = initialPayload.filters.map((f, index) => {
                        const fieldConfig = availableFields.find(af => af.key === f.key);
                        return {
                            id: Date.now() + index,
                            key: f.key,
                            label: fieldConfig ? fieldConfig.label : f.key,
                            type: fieldConfig ? fieldConfig.type : (f.type === 'string' ? 'text' : f.type),
                            op: f.op,
                            values: (f.values || []).map(v => Array.isArray(v) && v.length > 0 ? v[0] : v),
                            allowedOperators: fieldConfig ? fieldConfig.operators : []
                        };
                    });
                }


                if (initialPayload.date_range && initialPayload.date_range.length > 0) {
                    initialPayload.date_range.forEach((dr, index) => {
                        if (!restoredFilters.some(rf => rf.key === dr.key)) {
                            const fieldConfig = availableFields.find(af => af.key === dr.key);
                            restoredFilters.push({
                                id: Date.now() + 1000 + index, // Offset to avoid ID collision
                                key: dr.key,
                                label: fieldConfig ? fieldConfig.label : dr.key,
                                type: 'date',
                                op: dr.op || 'between',
                                values: [dr.from, dr.to],
                                allowedOperators: fieldConfig ? fieldConfig.operators : ['between', 'equals']
                            });
                        }
                    });
                }
                
                if (initialPayload.bucket && initialPayload.bucket !== 'active' && initialPayload.bucket !== 'all') {
                    if (!restoredFilters.some(rf => rf.key === 'bucket')) {
                        const fieldConfig = availableFields.find(af => af.key === 'bucket');
                        if (fieldConfig) {
                            restoredFilters.push({
                                id: Date.now() + 2000,
                                key: 'bucket',
                                label: fieldConfig.label || 'Bucket',
                                type: fieldConfig.type || 'text',
                                op: (fieldConfig.operators && fieldConfig.operators.length > 0) ? fieldConfig.operators[0] : 'in',
                                values: [initialPayload.bucket],
                                allowedOperators: fieldConfig.operators || ['in', 'equals']
                            });
                        }
                    }
                }
                
                if (restoredFilters.length > 0) {
                    setFilters(restoredFilters);
                }


                if (initialPayload.filter_opertaion || initialPayload.filtersOperator) {
                    setFiltersOperator(initialPayload.filter_opertaion || initialPayload.filtersOperator);
                }
                if (initialPayload.search !== undefined && !searchText) {
                    setSearchText(initialPayload.search);
                }
                if (initialPayload.sort) {
                    setSortConfig(initialPayload.sort);
                }
                if (initialPayload.pagination) {
                    setPagination(initialPayload.pagination);
            }
        }
    }, [initialPayload, availableFields]);

    /**
     * Reset filters when module changes (only if no initialPayload)
     */
    useEffect(() => {
        if (!initialPayload) {
            setFilters([{
                id: Date.now(),
                key: '',
                label: '',
                type: '',
                op: '',
                values: [],
                allowedOperators: []
            }]);
            setSearchText('');
            setSortConfig({
                key: (defaultSortOptions && defaultSortOptions.length > 0) ? defaultSortOptions[0].value : 'created_on',
                order: 'desc'
            });
            const defaultLimit = initialPayload?.pagination?.limit || (isIndentModule ? 10 : 50);
            setPagination({ skip: 0, limit: defaultLimit });
        }
    }, [module, initialPayload]);



    /**
     * Add a new filter condition row
     */
    const addFilterRow = () => {
        const newFilter = {
            id: Date.now(),
            key: '',
            label: '',
            type: '',
            op: '',
            values: [],
            allowedOperators: []
        };
        setFilters([...filters, newFilter]);
    };

    /**
     * Remove a filter condition row
     */
    const removeFilterRow = (id) => {
        if (filters.length > 1) {
            setFilters(filters.filter(f => f.id !== id));
        }
    };

    /**
     * Update an individual filter condition row
     */
    const updateFilter = (id, updatedFilter) => {
        setFilters(filters.map(f => f.id === id ? { ...f, ...updatedFilter } : f));
    };

    /**
     * Build the final payload for the parent component/API call
     */
    const buildFilterPayload = () => {
        // Filter out incomplete rows
        const validFilters = filters.filter(f => f.key && f.op);
        const bucketFilter = validFilters.find(f => f.key === 'bucket' && f.values.length > 0);
        
        const isBucketFieldAvailable = availableFields.some(af => af.key === 'bucket');
        const isSpot = module === 'spot';
        const hasAdhocIdFilter = validFilters.some(f => f.key === 'adhoc_id');
        const defaultBucketValue = (isSpot && hasAdhocIdFilter) ? 'all' : 'active';
        const finalBucket = bucketFilter ? bucketFilter.values[0] : (isBucketFieldAvailable ? defaultBucketValue : (extraPayload.bucket || defaultBucketValue));

        const otherValidFilters = validFilters.filter(f => f.key !== 'bucket');

        return {
            ...extraPayload,
            bucket: finalBucket,
            ...(module === 'spot' && { isBucketExplicitlySet: !!bucketFilter }),
            filtersOperator: filtersOperator,
            filters: otherValidFilters.map(({ id, allowedOperators, key: fKey, type, db_field, values, ...rest }) => {
                let finalValues = values;
                if (finalValues.length === 0) {
                    if (fKey === 'lr_uploaded') finalValues = ['lr'];
                    else if (fKey === 'pod_uploaded') finalValues = ['pod'];
                    else if (fKey === 'invoice_uploaded') finalValues = ['invoice'];
                }

                return {
                    ...rest,
                    key: fKey,
                    type: type === 'text' ? 'string' : type,
                    values: type === 'number' 
                        ? finalValues.map(v => (v === '' || v === null) ? 0 : Number(v)) 
                        : finalValues.map(v => Array.isArray(v) && v.length > 0 ? v[0] : v) 
                };
            }),
            search: searchText,
            sort: sortConfig,
            pagination,
            view: viewSelection
        };
    };

    /**
     * Fetch unique values for a specific field for dropdowns
     * Now using React Query for caching
     */
    const handleFetchUniqueValues = async (key) => {
        // Get all valid filters to send as context for unique values
        const allValidFilters = filters
            .filter(f => f.key && f.op && (f.op === 'exist' || f.op === 'not exist' || (Array.isArray(f.values) ? f.values.length > 0 : f.values)))
            .filter(f => f.key !== key);

        const payload = {
            ...extraPayload,
            bucket: module === 'spot' ? 'all' : (extraPayload.bucket || 'active'),
            key,
            view: viewSelection,
            filtersOperator: filtersOperator,
            search: searchText,
            filters: allValidFilters
                .map(({ id, allowedOperators, key: fKey, type, db_field, values, ...rest }) => ({
                    ...rest,
                    key: fKey,
                    type: type === 'text' ? 'string' : type,
                    values: type === 'number' ? values.map(v => (v === '' || v === null) ? 0 : Number(v)) : values
                }))
                .sort((a, b) => a.key.localeCompare(b.key)), // Stable order for query key
            date_range: []
        };


        const uniqueValuesParams = uniqueValuesMasterType
            ? `master_type=${uniqueValuesMasterType}&action=unique_values`
            : `${filterField ? `field=${filterField}&` : ''}module=${module}&action=unique_values`;

        const requestPayload = { ...payload };
        delete requestPayload.company_id;
        delete requestPayload.filter_opertaion;

        return queryClient.fetchQuery({
            queryKey: ['uniqueValues', module, filterField, uniqueValuesMasterType, key, JSON.stringify(payload)],
            queryFn: async () => {
                const response = await axios.post(`${filtersBaseUrl}?${uniqueValuesParams}`, requestPayload);
                const rawValues = response.data.values || [];
                // Flatten nested arrays if they exist (e.g., for truck_type)
                return rawValues.map(v => Array.isArray(v) && v.length > 0 ? v[0] : v);
            },
            staleTime: 0, // Always fetch fresh unique values when modified
        });
    };

    /**
     * Check if at least one filter has a field selected or search text exists
     */
    const hasValidFilters = filters.some(f => f.key && f.op) || searchText.trim().length > 0;

    /**
     * Execute Filter
     */
    const handleApplyFilters = () => {
        const isIndentModule = module && (module.includes('indent_tracking') || module.includes('indent_request') || module.includes('indent') || module === 'spot');
        
        if (isIndentModule) {
            for (const filter of filters) {
                if (filter.type === 'date' && filter.op === 'between' && filter.values[0] && filter.values[1]) {
                    const start = moment(filter.values[0]);
                    const end = moment(filter.values[1]);
                    
                    if (end.isAfter(moment(start).add(3, 'months'))) {
                        toast.error(`Date range for ${filter.label || filter.key} cannot exceed 3 months.`);
                        return;
                    }
                }
            }
        }

        const payload = buildFilterPayload();
        // Reset to first page on new filter apply
        if (payload.pagination) {
            payload.pagination.skip = 0;
            setPagination(prev => ({ ...prev, skip: 0 }));
        }
        if (onFilterApply) {
            onFilterApply(payload);
        }
    };

    /**
     * Reset everything
     */
    const handleReset = () => {
        setFilters([{
            id: Date.now(),
            key: '',
            label: '',
            type: '',
            op: '',
            values: [],
            allowedOperators: []
        }]);
        setSearchText('');
        setSortConfig({
            key: (defaultSortOptions && defaultSortOptions.length > 0) ? defaultSortOptions[0].value : 'created_on',
            order: 'desc'
        });
        setPagination({ skip: 0, limit: isIndentModule ? 10 : 50 });
        
        if (viewFields && viewFields.length > 0) {
            setViewSelection(viewFields[0].value);
        } else {
            setViewSelection('');
        }

        if (onReset) {
            onReset();
        }
    };

    return (
        <>
            {/* Backdrop for the drawer effect */}
            <div className="filter-drawer-backdrop" onClick={onClose}></div>

            <div className="multi-select-filter-container">
                <div className="filter-header">
                    <div className="header-left">
                        <h3 className="filter-title">
                            <i className="fa fa-filter filter-icon-fa" />
                            Advanced Filters
                        </h3>
                    </div>
                    <div className="header-actions">
                        <button
                            className="reset-btn-subtle"
                            onClick={handleReset}
                            disabled={isLoading}
                        >
                            <i className="fa fa-refresh mr-1" />
                            Reset
                        </button>
                        {onClose && (
                            <button className="close-filter-btn" onClick={onClose}>
                                <i className="fa fa-times" />
                            </button>
                        )}
                    </div>
                </div>

                {/* View Selection at the top */}
                {showViewSelection && viewFields && viewFields.length > 0 && (
                    <div className="search-container">
                        <div className="view-control-top d-flex align-items-center" style={{ gap: '10px' }}>
                            <label className="control-label mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>View:</label>
                            <select
                                className="sort-select"
                                style={{ flex: 1, height: '40px' }}
                                value={viewSelection}
                                onChange={(e) => setViewSelection(e.target.value)}
                            >
                                {viewFields.map((opt) => (
                                    <option key={opt.key} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}


                {/* Filter Rows Container */}
                <div className="filter-rows-container">
                    {filters.map((filter, index) => (
                        <div key={filter.id} className="filter-row-wrapper">
                            {index > 0 && (
                                <div className="filter-operator-connector">
                                    <span className="operator-badge">{filtersOperator}</span>
                                </div>
                            )}
                            <FilterRow
                                module={module}
                                filter={filter}
                                availableFields={availableFields.filter(f => !hiddenFields.includes(f.key) && (f.key === filter.key || !filters.some(otherF => otherF.id !== filter.id && otherF.key === f.key)))}
                                onUpdate={(updatedFilter) => updateFilter(filter.id, updatedFilter)}
                                onRemove={() => removeFilterRow(filter.id)}
                                canRemove={filters.length > 1}
                                onFetchValues={handleFetchUniqueValues}
                                hasOtherFilters={filters.some(f => f.id !== filter.id && f.key && f.op) || (searchText && searchText.trim().length > 0)}
                            />
                        </div>
                    ))}

                    <button className="add-filter-btn" onClick={addFilterRow}>
                        <i className="fa fa-plus-circle mr-1" />
                        Add Condition
                    </button>
                </div>

                {/* Sort and View Controls */}
                <div className="controls-container">
                    <div className="sort-control">
                        <label className="control-label">Sort By:</label>
                        <select
                            className="sort-select"
                            value={sortConfig.key}
                            onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })}
                        >
                            {availableSorts.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="sort-order-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSortConfig({ ...sortConfig, order: sortConfig.order === 'asc' ? 'desc' : 'asc' });
                            }}
                            title={sortConfig.order === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            <i className={`fa fa-sort-amount-${sortConfig.order === 'asc' ? 'up' : 'down'}`} />
                            <span className="sort-order-label">
                                {sortConfig.order === 'asc' ? 'ASC' : 'DESC'}
                            </span>
                        </button>
                    </div>
                    {!hideItemsPerPage && (
                    <div className="pagination-control">
                        <label className="control-label">Items per page:</label>
                        <select
                            className="pagination-select"
                            value={pagination.limit}
                            onChange={(e) => setPagination({ ...pagination, limit: parseInt(e.target.value) })}
                        >
                            {isIndentModule ? (
                                <>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </>
                            ) : (
                                <>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="500">500</option>
                                </>
                            )}
                        </select>
                    </div>
                    )}

                </div>

                {/* Apply Button Section */}
                <div className="filter-actions">
                    <button
                        className="apply-filters-btn"
                        onClick={handleApplyFilters}
                        disabled={isLoading || isConfigQueryLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="spinner"></div>
                                Applying...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check-circle mr-2" />
                                Apply Filters
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default MultiSelectFilterUI;
